import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SprayCan, Wrench, ArrowRight } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { useLanguage } from '../contexts/LanguageContext';
interface WelcomeScreenProps {
  onSelectCategory: (category: string) => void;
}
export function WelcomeScreen({
  onSelectCategory
}: WelcomeScreenProps) {
  const {
    t
  } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const categories = [{
    id: 'cleaning',
    icon: SprayCan,
    title: t.welcome.categories.cleaning.title,
    desc: t.welcome.categories.cleaning.desc
  }, {
    id: 'repair',
    icon: Wrench,
    title: t.welcome.categories.repair.title,
    desc: t.welcome.categories.repair.desc
  }];
  const handleCategoryClick = (id: string) => {
    if (selectedCategory === id) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(id);
    }
  };
  const handleStartReport = () => {
    if (selectedCategory) {
      onSelectCategory(selectedCategory);
    }
  };
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="max-w-4xl mx-auto px-6 relative min-h-[80vh] flex flex-col" data-id="element-601">
      <div className="text-center mb-12 mt-12" data-id="element-602">
        <div className="text-5xl mb-4 animate-bounce-slow inline-block" data-id="element-603">
          {t.welcome.greeting}
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-navy mb-3" data-id="element-604">
          {t.welcome.title}
        </h2>
        <p className="text-gray-500 text-xl" data-id="element-605">{t.welcome.subtitle_translated}</p>
      </div>

      <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto w-full" data-id="element-606">
        {categories.map(cat => {
        const isSelected = selectedCategory === cat.id;
        return <motion.div key={cat.id} variants={item} data-id="element-607">
              <Card className={`relative h-full min-h-[200px] p-8 flex flex-col items-center text-center cursor-pointer transition-all duration-300 ${isSelected ? 'ring-2 ring-teal-deep bg-teal-50 shadow-md' : 'bg-white border border-gray-200 hover:shadow-md hover:border-gray-300'}`} onClick={() => handleCategoryClick(cat.id)} data-id="element-608">
                <div className="mb-6 transition-transform duration-300" data-id="element-609">
                  <cat.icon className={`w-14 h-14 transition-colors duration-300 ${isSelected ? 'text-teal-deep' : 'text-gray-400'}`} strokeWidth={1.5} data-id="element-610" />
                </div>

                <h3 className={`text-2xl font-bold mb-2 transition-colors ${isSelected ? 'text-teal-deep' : 'text-navy'}`} data-id="element-611">
                  {cat.title}
                </h3>
                <p className={`text-base transition-colors ${isSelected ? 'text-teal-800' : 'text-gray-500'}`} data-id="element-612">
                  {cat.desc}
                </p>
              </Card>
            </motion.div>;
      })}
      </motion.div>

      {/* Floating CTA Button */}
      <AnimatePresence data-id="element-613">
        {selectedCategory && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-8 right-8 z-50" data-id="element-614">
            <Button onClick={handleStartReport} className="h-14 px-8 text-lg bg-teal-deep hover:bg-teal-700 text-white font-semibold shadow-xl flex items-center gap-3 rounded-xl" data-id="element-615">
              {t.common.startReport}
              <ArrowRight className="w-5 h-5" data-id="element-616" />
            </Button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}